import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { initUseQuery, queryKey } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";

import type { BoardProjectEvent } from "../types";
import type { IProjectIdeaEvent } from "./ideaEventUtils";

type BoardEventsPage = OpenAPI.GET<"/boards/{for_id}/events">["response"];

export const getBoardEventsQueryKey = (
  forId: string,
  options?: { ideaId?: string; limit?: number },
) =>
  queryKey(api.boards.getBoardEvents, [
    forId,
    {
      limit: options?.limit ?? 10,
      ...(options?.ideaId ? { idea_id: options.ideaId } : {}),
    },
  ]);

export const useBoardEventsInfiniteQuery = ({
  forId,
  ideaId,
  limit = 10,
  enabled = true,
}: {
  forId?: string;
  ideaId?: string;
  limit?: number;
  enabled?: boolean;
}) => {
  const eventsQuery = useInfiniteQuery({
    queryKey: forId
      ? getBoardEventsQueryKey(forId, { ideaId, limit })
      : ["boardEvents", "disabled"],
    queryFn: ({ pageParam }) =>
      initUseQuery(api.boards.getBoardEvents, [
        forId!,
        { page: pageParam, limit, ...(ideaId ? { idea_id: ideaId } : {}) },
      ]).queryFn(),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.canExtend ? lastPageParam + 1 : undefined,
    enabled: enabled && !!forId,
    placeholderData: keepPreviousData,
  });

  const events =
    eventsQuery.data?.pages.flatMap(
      (page) => page.events as IProjectIdeaEvent[],
    ) ?? [];

  const seen = eventsQuery.data?.pages[0]?.seen ?? true;

  return { ...eventsQuery, events, seen };
};

export const updateBoardEventInCache = (
  data: InfiniteData<BoardEventsPage> | undefined,
  eventId: string,
  userId: string,
) => {
  if (!data) return data;

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      events: page.events.map((event) => {
        if (event._id !== eventId) return event;
        const boardEvent = event as BoardProjectEvent;
        return {
          ...boardEvent,
          interacted: [...(boardEvent.interacted ?? []), userId],
        };
      }),
    })),
  };
};
